import {
  Box,
  Grid,
  IconButton,
  Tooltip
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { LucideSparkles } from "lucide-react";
import { Fragment, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenu } from "./DropdownMenu.component";
import { MenuIcon } from "./MenuIcon.component";
import { NotificationsIcon } from "./NotificationsIcon.component";
import { ProfileIcon } from "./ProfileIcon.component";
import oliviaIcon from "../../../assets/oliviaIcon.png";
import { Button, Icon } from "../../../components";
import { useAISupportChat } from "../../../context/useAISupportChat";
import { useAuthStore } from "../../../stores";
import { getEnvironmentVariables, checkCustomLogicFe } from "../../../utils/general";
import { DROPDOWN, type Dropdown } from "../Header.consts";

type HeaderActionsBarProps = {
  variant: "desktop" | "mobile"
  isMultilanguage: boolean
  showTutorialUserMenu?: boolean
  dropdown: Dropdown | null
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
  anchorElDropdown: HTMLElement | null
  setAnchorElDropdown: Dispatch<SetStateAction<HTMLElement | null>>
  setAnchorElNotify?: Dispatch<SetStateAction<HTMLElement | null>>
  setLanguageModalOpen: Dispatch<SetStateAction<boolean>>
  setImpersonationModalOpen: Dispatch<SetStateAction<boolean>>
  leadingElement?: ReactNode
}

export function HeaderActionsBar({
  variant,
  isMultilanguage,
  showTutorialUserMenu,
  dropdown,
  setDropdown,
  anchorElDropdown,
  setAnchorElDropdown,
  setAnchorElNotify,
  setLanguageModalOpen,
  setImpersonationModalOpen,
  leadingElement
}: HeaderActionsBarProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { openChat: openSupportChat } = useAISupportChat();
  const userData = useAuthStore(state => state.userData);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const isImpersonation = useAuthStore(state => state.isImpersonation);
  const isAiChatAvailable = userData?.permissions.includes("agent_ai.chat.postlogin");

  const { customLogicFe } = getEnvironmentVariables();
  const hideNotifications = checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hide_notifications" });

  const isDesktop = variant === "desktop";

  return (
    <Fragment>
      <Box
        display="flex"
        alignItems="center"
        height={ "2.5rem" }
        gap={ 3 }
        sx={ isDesktop ? { justifyContent: "flex-end" } : { justifyContent: "flex-end", width: "100%" } }
      >
        { isImpersonation &&
          <Tooltip title={ t("end-impersonation-title") }>
            <IconButton
              aria-label={ isDesktop ? t("end-impersonation-title") : undefined }
              sx={ {
                cursor: "pointer",
                zIndex: "1050"
              } }
              onClick={ () => setImpersonationModalOpen(true) }
            >
              <Icon
                icon={ "almost_there" }
                size={ "1.5rem" }
                color={ theme.customColors.headerIcon }
              />
            </IconButton>
          </Tooltip>
        }
        { leadingElement }
        { /* chatboticon */ }
        { isDesktop && smartConfiguration?.chatbot ? (
          <Button
            sx={ {
              "&:hover": {
                background: "transparent"
              },
              color: theme.customColors.headerIcon,
              height: "100%",
              minWidth:"unset",
              padding: "0",
              position:"relative",
              zIndex: theme.zIndex.fab
            } }
            onClick={ ()=> { setDropdown(DROPDOWN.CHATBOT) } }>
            <LucideSparkles
              aria-label={ t("learning_coach") }
              size={ 24 }
              color={ theme.customColors.headerIcon }
            />
          </Button>
        ) : null }
        { /* support ai icon */ }
        { isAiChatAvailable && (
          <Button
            id="chatbot"
            aria-label={ t("ai_support_chat_title") }
            sx={ {
              "&:hover": {
                opacity: 0.85
              },
              borderRadius: "8px",
              height: "100%",
              minWidth: "unset",
              padding: "4px",
              position: "relative",
              zIndex: theme.zIndex.fab
            } }
            onClick={ () => { openSupportChat() } }>
            <img
              src={ oliviaIcon }
              alt=""
              aria-hidden="true"
              width={ 32 }
              height={ 32 }
            />
          </Button>
        ) }
        { /* notifications icon */ }
        { !hideNotifications && (
          <NotificationsIcon
            showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
            setAnchorElNotify={ setAnchorElNotify }
            setDropdown={ setDropdown }
          />
        ) }
        { /* avatar group */ }
        <Grid
          display={ "flex" }
          justifyContent={ "space-between" }
          gap={ isDesktop ? 2 : 1 }
          zIndex={ theme.zIndex.fab }
        >
          <ProfileIcon />
          <MenuIcon
            showDropdownMenu={ dropdown === DROPDOWN.MENU }
            setAnchorElDropdown={ setAnchorElDropdown }
            setDropdown={ setDropdown }
          />
        </Grid>
      </Box>
      { /* dropdown menu */ }
      <DropdownMenu
        setLanguageModalOpen={ setLanguageModalOpen }
        isMultilanguage={ isMultilanguage }
        anchorElDropdown={ anchorElDropdown }
        hideMenu={ () => setDropdown(null) }
        showChatBot={ () => setDropdown(DROPDOWN.CHATBOT) }
        showDropdownMenu={ dropdown === DROPDOWN.MENU }
        showTutorialUserMenu={ !isDesktop ? showTutorialUserMenu : undefined }
      />
    </Fragment>
  );
}
